import { Dispatch, Action } from 'redux';
import { AppState } from '..';
import { requestAction, doAsync } from '@common/components/common.actions';
import { cviService } from '@services/CVIService';
import {
  AuthorizationPromptViewModel,
  AuthorizationPromptAnswerViewModel,
  AuthorizationPromptResponseViewModel,
  AuthoVersionViewModel
} from '@models/AuthorizationPrompt';
import { AuthorizationState } from './authorization.reducer';
import { Dictionary } from '@models/common.models';
import { AuthReportFormViewModel } from '@models/AuthReportSummary';
import { authorizationService } from '@services/AuthorizationService';
import {
  SaveAuthorizationFormProgressAction,
  AuthActionType,
  setAuthorizationReportId,
  SaveAuthoDiscountAction
} from './authorization.actions';
import * as uuid from 'uuid/v4';
import FormNames from '@constants/FormNames.enum';
import { actions } from 'react-redux-form';
import { AuthorizationPromptTypes } from '../_models/AuthorizationPrompt';

export enum AuthorizationDetailFormActionType {
  SetCVIPromptsLoading = 'SetCVIPromptsLoading',
  SetCVIPrompts = 'SetCVIPrompts',
  ChangeMultiPromptLineItem = 'ChangeMultiPromptLineItem',
  AddMultiPromptLineItem = 'AddMultiPromptLineItem',
  SetAuthoVersions = 'SetAuthoVersions',

  SetPromptResponse = 'AddPromptReponse',
  SetPromptResponses = 'SetPromptResponses',
  SetFormSubmittingFlag = 'SetFormSubmittingFlag'
}

export interface SetAuthoriztionFormSubmittingFlagAction extends Action {
  readonly type: AuthorizationDetailFormActionType.SetFormSubmittingFlag;
  readonly submitting: boolean;
}

export interface SetPromptResponsesAction extends Action {
  readonly type: AuthorizationDetailFormActionType.SetPromptResponses;
  readonly responses: Array<AuthorizationPromptAnswerViewModel>;
}

export interface SetCVIPromptsAction extends Action {
  readonly type: AuthorizationDetailFormActionType.SetCVIPrompts;
  readonly prompts: Array<AuthorizationPromptViewModel>;
}

export interface ChangeMultiPromptLineItemAction extends Action {
  readonly type: AuthorizationDetailFormActionType.ChangeMultiPromptLineItem;
  readonly parentPrompt: AuthorizationPromptViewModel;
  readonly childUUID?: string;
  readonly remove: boolean | undefined;
}

export interface AddMultiLinePromptLineAction extends Action {
  readonly type: AuthorizationDetailFormActionType.AddMultiPromptLineItem;
  readonly parentPrompt: AuthorizationPromptViewModel;
  readonly childrenToAdd: Array<MultiLineAddStruct>;
}

export interface SetPromptRepsonseAction extends Action {
  readonly type: AuthorizationDetailFormActionType.SetPromptResponse;
  readonly response: AuthorizationPromptAnswerViewModel;
}

export interface SetCVIPromptsLoadingAction extends Action {
  readonly type: AuthorizationDetailFormActionType.SetCVIPromptsLoading;
  readonly loading: boolean;
}

export interface SetAuthoVersionsAction {
  readonly type: AuthorizationDetailFormActionType.SetAuthoVersions;
  readonly result: AuthoVersionViewModel[];
}

export function loadCVIPromptAnswers(swoId: number, cviCd: number, version?: number) {
  return async (dispatch: Dispatch<AppState>) => {
    return dispatch(
      requestAction(() => authorizationService.getAuthoReportFormAnswers(swoId, cviCd, version), loadAnswersIntoForm, {
        supressErrors: true
      })
    );
  };
}

export function setAuthoVersions(authoVersions: AuthoVersionViewModel[]): SetAuthoVersionsAction {
  return {
    type: AuthorizationDetailFormActionType.SetAuthoVersions,
    result: authoVersions
  };
}

export function loadAuthoVersions(reportId: number, swoDetail: number) {
  return async (dispatch: Dispatch<AppState>) => {
    return dispatch(
      requestAction(() => authorizationService.getAuthoReportVersions(reportId, swoDetail), setAuthoVersions)
    );
  };
}

export function loadAnswersIntoForm(report: AuthReportFormViewModel): any {
  return async (dispatch: Dispatch<AppState>, getState: () => AppState) => {
    const state = getState().authorization;
    const answers = report.responses;
    const currentPrompts = state.form.prompts;
    let prompts: AuthorizationPromptViewModel[] = [];
    let answersToLoad: AuthorizationPromptAnswerViewModel[] = [];
    dispatch(setAuthorizationReportId(report.reportID));
    dispatch(loadAuthoVersions(report.reportID, report.swoDetailNr));
    currentPrompts.forEach(prompt => {
      if (prompt.canGrow && prompt.childPrompts && prompt.childPrompts.length > 0) {
        const children = prompt.childPrompts;
        let newChildren: {
          child: any;
          uuid: string;
          response: AuthorizationPromptResponseViewModel;
        }[] = [];
        children.forEach(child => {
          const responses = answers.filter(x => x.promptId === child.promptId);
          for (let i = 0; i < responses.length; i++) {
            if (i === 0) {
              answersToLoad.push({
                promptUUID: child.uuid,
                responseId: responses[i].responseId,
                response: responses[i].response,
                notCovered: responses[i].notCovered,
                isEstimate: responses[i].isEstimate,
                payeeType: '' + responses[i].payeeType,
                promptType: child.type,
                responseChanged: responses[i].responseChanged,
                previousResponse: responses[i].previousResponse
              });
              continue;
            }
            try {
              let childUUID = uuid();
              newChildren.push({
                child: child,
                response: responses[i],
                uuid: childUUID
              });
              answersToLoad.push({
                promptUUID: childUUID,
                response: responses[i].response,
                responseId: responses[i].responseId,
                notCovered: responses[i].notCovered,
                isEstimate: responses[i].isEstimate,
                payeeType: '' + responses[i].payeeType,
                promptType: child.type,
                responseChanged: responses[i].responseChanged,
                previousResponse: responses[i].previousResponse
              });
            } catch (e) {
              throw e;
            }
          }
          return;
        });
        newChildren.sort((a, b) => {
          return a.response.qOrder - b.response.qOrder;
        });
        for (let i = 0; i < newChildren.length; i += children.length) {
          let kids: MultiLineAddStruct[] = newChildren.slice(i, i + children.length).map(x => {
            return { uuid: x.uuid, prompt: x.child };
          });
          dispatch(addMultiLinePromptLine(prompt, kids));
        }

        return;
      }
      const response = answers.find(x => x.promptId === prompt.promptId);
      if (response != null) {
        prompts.push(prompt);
        answersToLoad.push({
          promptUUID: prompt.uuid,
          responseId: response.responseId,
          response: response.response,
          notCovered: response.notCovered,
          isEstimate: response.isEstimate,
          payeeType: response.payeeType,
          promptType: prompt.type,
          responseChanged: response.responseChanged,
          previousResponse: response.previousResponse
        });
      }
      return;
    });

    let results = {};

    answersToLoad.forEach(answer => {
      results[answer.promptUUID] =
        answer.promptType === AuthorizationPromptTypes.List ? answer.responseId : answer.response;
      if (answer.payeeType) {
        results[answer.promptUUID + 'payee'] = answer.payeeType;
      }
    });
    dispatch(setAuthorizationPromptResponses(answersToLoad));
    return dispatch(actions.change(FormNames.AuthorizationDetailForm + '.answers', results));
  };
}

export function setAuthorizationPromptResponses(
  responses: Array<AuthorizationPromptAnswerViewModel>
): SetPromptResponsesAction {
  return {
    type: AuthorizationDetailFormActionType.SetPromptResponses,
    responses: responses
  };
}

export function loadCVIPrompts(swoId: number, cviId: number, version?: number) {
  return async (dispatch: Dispatch<AppState>) => {
    return dispatch(doAsync(setCVIPrompts([])))
      .then(() => {
        return dispatch(
          requestAction(() => cviService.getCVIPrompts(cviId), setCVIPrompts, {
            loadingAction: setAuthorizationFormLoading
          })
        ).then(x => {
          dispatch(loadCVIPromptAnswers(swoId, cviId, version));
          return x;
        });
      })
      .catch(e => {
        throw e;
      });
  };
}

export function setCVIPrompts(prompts: Array<AuthorizationPromptViewModel>): SetCVIPromptsAction {
  return {
    type: AuthorizationDetailFormActionType.SetCVIPrompts,
    prompts: prompts
  };
}
interface MultiLineAddStruct {
  uuid?: string;
  prompt: AuthorizationPromptViewModel;
}
export function addMultiLinePromptLine(
  parentPrompt: AuthorizationPromptViewModel,
  childrenToAdd: MultiLineAddStruct[]
): AddMultiLinePromptLineAction {
  return {
    type: AuthorizationDetailFormActionType.AddMultiPromptLineItem,
    parentPrompt: parentPrompt,
    childrenToAdd: childrenToAdd
  };
}

export function changeMultiPromptLineItem(
  parentPrompt: AuthorizationPromptViewModel,
  remove?: boolean | undefined,
  childUUID?: string
): ChangeMultiPromptLineItemAction {
  return {
    type: AuthorizationDetailFormActionType.ChangeMultiPromptLineItem,
    parentPrompt: parentPrompt,
    childUUID: childUUID,
    remove: remove
  };
}

export function setPromptResponse(
  prompt: AuthorizationPromptViewModel,
  oldResponse: AuthorizationPromptAnswerViewModel | undefined,
  response: string
): SetPromptRepsonseAction {
  let responseId = 0;

  if (prompt.type === AuthorizationPromptTypes.YesNo) {
    if (response.indexOf('yes') > -1) {
      responseId = -1;
    } else {
      responseId = 0;
    }
  } else if (prompt.type === AuthorizationPromptTypes.List) {
    var opt = prompt.options.find(x => x.id === +response);
    responseId = +response;
    response = opt ? opt.value : '';
  }

  return {
    type: AuthorizationDetailFormActionType.SetPromptResponse,
    response: {
      ...oldResponse,
      response: response,
      promptUUID: prompt.uuid,
      responseId: responseId
    }
  };
}

export function doSaveAuthorizationForm(swoId: number, form?: { answers?: { [property: string]: string } }) {
  return async (dispatch: Dispatch<AppState>, getState: () => AppState) => {
    const authorizationState = getState().authorization;
    const authoFormState = authorizationState.form;
    const reportId = authorizationState.reportId;
    if (
      !authorizationState ||
      !form ||
      !form.answers ||
      !(form.answers.length === undefined) ||
      !authorizationState.selectedCvi
    ) {
      return;
    }
    let availableIndex = 0;
    let result: AuthReportFormViewModel = {
      reportID: reportId !== 0 ? reportId : 0,
      swoId: swoId,
      swoDetailNr: authorizationState.selectedCvi.swoDetailNumber,
      responses: [],
      overrideDiscount: authorizationState.overrideDiscount
    };
    const answerKeys = Object.keys(form.answers);
    // iterate over the prompts, if the prompt has a uuid value in the answers already, map it to the resulting array. if it doesn't, try to pull it from the state
    authoFormState.prompts.forEach(prompt => {
      if (prompt.childPrompts && prompt.childPrompts.length && prompt.childPrompts.length > 0) {
        let subIds = {}; // track how many instances of a particular child prompt exist for 'Multi' elements
        prompt.childPrompts.forEach(child => {
          if (subIds[child.promptId] === undefined) {
            subIds[child.promptId] = 0;
          }
          const cr = parsePromptResponse(child, answerKeys, authorizationState, form.answers!);
          if (cr !== undefined) {
            result.responses.push({
              promptId: child.promptId,
              qOrder: availableIndex++, // still give it a global sort index
              response: cr.response,
              responseId: cr.responseId,
              subId: subIds[child.promptId]++, // also give it a sub index
              cviCode: authorizationState.selectedCvi!.cvi.id,
              notCovered: cr.notCovered !== undefined ? cr.notCovered : child.notCovered || false,
              isEstimate: cr.isEstimate !== undefined ? cr.isEstimate : child.isEstimate || false,
              payeeType: cr.payeeType ? cr.payeeType.toString() : '1',
              updatesField: child.updatesField
            });
          }
        });
        return; // if this prompt had children, then it has no top level value, skip it
      }

      const r = parsePromptResponse(prompt, answerKeys, authorizationState, form.answers!);

      if (r !== undefined) {
        result.responses.push({
          promptId: prompt.promptId,
          qOrder: availableIndex++, // still give it a global sort index
          response: r.response,
          responseId: r.responseId,
          cviCode: authorizationState.selectedCvi!.cvi.id,
          notCovered: r.notCovered !== undefined ? r.notCovered : prompt.notCovered || false,
          isEstimate: r.isEstimate !== undefined ? r.isEstimate : prompt.isEstimate || false,
          payeeType: r.payeeType ? r.payeeType.toString() : '1',
          updatesField: prompt.updatesField
        });
      }
    });
    return dispatch(
      requestAction(
        () => authorizationService.saveReportSummary(result.swoId, result, authoFormState.isSubmitting),
        saveAuthorizationFormProgress,
        {
          hasToast: true,
          toastSuccessMessage: authoFormState.isSubmitting
            ? 'Authorization Submitted Successfully.'
            : 'Progress saved successfully.'
        }
      )
    );
  };
}

function saveAuthorizationFormProgress(response: AuthReportFormViewModel): SaveAuthorizationFormProgressAction {
  return {
    type: AuthActionType.SaveAuthorizationFormProgress,
    response: response
  };
}

function parsePromptResponse(
  prompt: AuthorizationPromptViewModel,
  answerKeys: string[],
  authorizationState: AuthorizationState,
  answers: Dictionary<string>
): AuthorizationPromptAnswerViewModel {
  const answer = answerKeys.find(a => a === prompt.uuid);
  const response = authorizationState.form.responses.find(x => x.promptUUID === prompt.uuid);
  return response || { promptUUID: '', responseId: 0, response: answer || '' };
}

export function setAuthorizationFormLoading(loading: boolean): SetCVIPromptsLoadingAction {
  return {
    type: AuthorizationDetailFormActionType.SetCVIPromptsLoading,
    loading: loading
  };
}

export function setFormSubmission(submitting: boolean) {
  return {
    type: AuthorizationDetailFormActionType.SetFormSubmittingFlag,
    submitting: submitting
  };
}

export function saveAuthoDiscount(value: number): SaveAuthoDiscountAction {
  return {
    type: AuthActionType.SaveAuthoDiscount,
    discount: value
  };
}

export function toggleNotCoveredPriceFlag(
  prompt: AuthorizationPromptViewModel,
  response: AuthorizationPromptAnswerViewModel
) {
  let nc = (response || {}).notCovered;
  let est = (response || {}).isEstimate;

  if (nc !== undefined && !nc) {
    est = false;
  }
  if (nc === undefined) {
    nc = prompt.notCovered;
  }
  nc = !nc;

  let newRes = {
    ...response,
    response: response ? response.response : '0',
    promptUUID: prompt.uuid,
    notCovered: nc
  };

  if (est !== undefined) {
    newRes.isEstimate = false;
  }

  return {
    type: AuthorizationDetailFormActionType.SetPromptResponse,
    response: {
      ...newRes
    }
  };
}

export function triggerPayeeTypeChange(
  prompt: AuthorizationPromptViewModel,
  response: AuthorizationPromptAnswerViewModel,
  newType: number
) {
  return {
    type: AuthorizationDetailFormActionType.SetPromptResponse,
    response: {
      ...response,
      response: response ? response.response : '0',
      promptUUID: prompt.uuid,
      payeeType: newType
    }
  };
}

export function toggleisEstimatePriceFlag(
  prompt: AuthorizationPromptViewModel,
  response: AuthorizationPromptAnswerViewModel
) {
  let est = (response || {}).isEstimate;
  let nc = (response || {}).notCovered;

  if (est !== undefined && !est) {
    nc = false;
  }
  if (est === undefined) {
    est = prompt.isEstimate;
  }
  est = !est;

  let newRes = {
    ...response,
    response: response ? response.response : '0',
    promptUUID: prompt.uuid,
    isEstimate: est
  };

  if (nc !== undefined) {
    newRes.notCovered = false;
  }

  return {
    type: AuthorizationDetailFormActionType.SetPromptResponse,
    response: {
      ...newRes
    }
  };
}



// WEBPACK FOOTER //
// ./src/authorization/authorization-form.actions.tsx